import {
  requestDefaults,
  toWireFlybuys,
} from 'scenes/common/api/quote/marshaller';
import { toWireExcesses } from 'scenes/common/api/quote/marshaller/excess';
import {
  getInitialMetaData,
  getPolicyDetailsMetadata,
} from 'scenes/common/api/quote/marshaller/metadata';
import {
  WireProduct,
  WireQuoteRequest,
  WireStatus,
} from 'scenes/common/api/quote/types';
import { toWireSaveQuoteEmailAddress } from 'scenes/common/api/quote/marshaller/policy';
import { getApiData } from 'scenes/common/helpers/apiData';
import { Excess } from 'scenes/common/types';
import {
  getFlybuysNumber,
  getCoverType,
  getSelectedExtras,
  isFlybuysMember,
} from 'scenes/landlord/selectors';
import {
  LandlordFunnelState,
  QuoteUserSelections,
} from 'scenes/landlord/types';
import { toWireCoverType } from 'scenes/common/api/quote/marshaller/coverage';
import { marshalQuoteDates } from 'scenes/common/api/quote/marshaller/dates';

import {
  WireLandlordQuoteCreateRequest,
  WireLandlordQuoteUpdateRequest,
} from '../types';
import { toWireAddresses } from './address';
import { toWireAssets } from './assets';
import { toWireCoverages } from './coverage';
import { toWireLandlordParties } from './party';
import { toWirePolicy } from './policy';

export const marshalQuoteCreateRequest = (
  state: LandlordFunnelState
): WireLandlordQuoteCreateRequest => ({
  ...requestDefaults(WireProduct.Landlord),
  addresses: toWireAddresses(state),
  policy: toWirePolicy(state),
  coverType: toWireCoverType(getCoverType(state)),
  parties: toWireLandlordParties(state),
  assets: toWireAssets(state),
  coverages: toWireCoverages(state),
  flybuys: toWireFlybuys(isFlybuysMember(state), getFlybuysNumber(state)),
  metaData: getInitialMetaData(),
});

const marshalQuoteUpdateRequest = (
  state: LandlordFunnelState,
  { buildingExcess, contentsExcess }: QuoteUserSelections,
  selectedExtras?: string[]
): WireLandlordQuoteUpdateRequest => {
  const { payload } = getApiData(state.apiResponses.quoteDetails);

  return {
    ...requestDefaults(WireProduct.Landlord),
    addresses: toWireAddresses(state),
    policy: {
      ...payload.policy,
      ...toWirePolicy(state),
    },
    coverType: toWireCoverType(getCoverType(state)),
    parties: toWireLandlordParties(state),
    assets: toWireAssets(state),
    excesses: toWireExcesses(buildingExcess, contentsExcess),
    coverages: toWireCoverages(
      state,
      [buildingExcess, contentsExcess],
      selectedExtras
    ),
    flybuys: toWireFlybuys(isFlybuysMember(state), getFlybuysNumber(state)),
    metaData: getInitialMetaData(),
  };
};

export const marshalQuoteDetailsUpdate = (
  funnelState: LandlordFunnelState
): WireQuoteRequest =>
  marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    getSelectedExtras(funnelState)
  );

export const marshalQuoteUpdateBuildingExcess = (
  funnelState: LandlordFunnelState,
  selectedBuildingExcess: Excess
): WireQuoteRequest => ({
  ...marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      buildingExcess: selectedBuildingExcess,
    },
    getSelectedExtras(funnelState)
  ),
  targetStatus: WireStatus.OptionalExtra,
});

export const marshalQuoteUpdateContentsExcess = (
  funnelState: LandlordFunnelState,
  selectedContentsExcess: Excess
): WireQuoteRequest => ({
  ...marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      contentsExcess: selectedContentsExcess,
    },
    getSelectedExtras(funnelState)
  ),
  targetStatus: WireStatus.OptionalExtra,
});

export const marshalQuoteUpdateOptionalExtras = (
  funnelState: LandlordFunnelState,
  selectedExtras: string[]
) => ({
  ...marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    selectedExtras
  ),
  targetStatus: WireStatus.OptionalExtra,
});

const marshalQuoteUpdateWithPolicyStatusMetadata = (
  funnelState: LandlordFunnelState,
  targetStatus: WireStatus
) => ({
  ...marshalQuoteUpdateRequest(
    funnelState,
    {
      ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
    },
    getSelectedExtras(funnelState)
  ),
  targetStatus,
  metaData: getPolicyDetailsMetadata(),
});

export const marshalQuoteUpdateToPolicyDetails = (
  funnelState: LandlordFunnelState
) =>
  marshalQuoteUpdateWithPolicyStatusMetadata(
    funnelState,
    WireStatus.PolicyDetails
  );

export const marshalQuoteUpdateToDataComplete = (
  funnelState: LandlordFunnelState
) =>
  marshalQuoteUpdateWithPolicyStatusMetadata(
    funnelState,
    WireStatus.DataComplete
  );

export const marshalQuoteUpdateSave = (
  funnelState: LandlordFunnelState,
  emailAddress: string
) => {
  const saveQuoteRequest = {
    ...marshalQuoteUpdateRequest(
      funnelState,
      {
        ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      },
      getSelectedExtras(funnelState)
    ),
    targetStatus: WireStatus.Save,
  };
  saveQuoteRequest.policy.otherAttributes.push(
    toWireSaveQuoteEmailAddress(emailAddress)
  );

  return saveQuoteRequest;
};

export const marshalQuoteUpdateEmail = (
  funnelState: LandlordFunnelState,
  emailAddress: string
) => {
  const saveQuoteRequest = {
    ...marshalQuoteUpdateRequest(
      funnelState,
      {
        ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      },
      getSelectedExtras(funnelState)
    ),
    targetStatus: WireStatus.Email,
  };
  saveQuoteRequest.policy.otherAttributes.push(
    toWireSaveQuoteEmailAddress(emailAddress)
  );

  return saveQuoteRequest;
};

export const marshalQuoteUpdatePolicyStartDate = (
  funnelState: LandlordFunnelState,
  policyStartDate: string
) => {
  const saveQuoteRequest = {
    ...marshalQuoteUpdateRequest(
      funnelState,
      {
        ...getApiData(funnelState.apiResponses.quoteDetails).userSelections,
      },
      getSelectedExtras(funnelState)
      // getSpecifiedContents(funnelState),
      // getUnspecifiedPortableContentsLimit(funnelState)
    ),
  };
  saveQuoteRequest.policy.dates = marshalQuoteDates(policyStartDate);
  return saveQuoteRequest;
};
